import {useEffect, useMemo} from "react";
import {
	useAnimate,
	usePresence,
	DOMKeyframesDefinition,
	AnimationOptions,
	useReducedMotion,
} from "framer-motion";

/**
 *
 * NOTE: The typing on this is kind of cursed
 *       but I blame framer because what's responsibility.
 * @param enterOptions
 * @param exitOptions
 * @param onEnterAnimationComplete
 * @param onExitAnimationComplete
 * @returns
 */

type AnimationConfiguration = [DOMKeyframesDefinition, AnimationOptions];

interface EnterExitAnimationArgs {
	enterAnimation: AnimationConfiguration;
	exitAnimation: AnimationConfiguration;
	onEnterTriggered?: () => void;
	onExitTriggered?: () => void;
	onEnterAnimationComplete?: () => void;
	onExitAnimationComplete?: () => void;
}

function useEnterExitAnimation(args: EnterExitAnimationArgs) {
	const [isPresent, safeToRemove] = usePresence();
	const [scope, animate] = useAnimate();

	const shouldReduceMotion = useReducedMotion();
	const enterAnimation: AnimationConfiguration[] = useMemo(() => {
		/**
		 * TODO: We allow only opacity transitions with reduced motion.  There may
		 * come a time when we wish to pass a different configuration altogether for the
		 * case of reduced motion.  This should suffice for now.
		 * */
		if (shouldReduceMotion) {
			const {opacity, ...rest} = args.enterAnimation[0];
			const animationSequence: AnimationConfiguration[] = [
				[{...rest}, {duration: 0}],
			];
			if (opacity) {
				animationSequence.push([
					{opacity},
					args.enterAnimation[1] ?? {duration: 0.3},
				]);
			}
			return animationSequence;
		}

		return [args.enterAnimation];
	}, [args, shouldReduceMotion]);
	const exitAnimation: AnimationConfiguration = useMemo(
		() =>
			shouldReduceMotion
				? [
						{opacity: args.exitAnimation[0]?.opacity},
						{duration: args.exitAnimation[1]?.duration ?? 0.3},
					]
				: args.exitAnimation,
		[args, shouldReduceMotion]
	);

	useEffect(() => {
		if (!scope.current) {
			return;
		}
		if (isPresent) {
			const animateEntrance = async () => {
				await animate(
					enterAnimation.map((animation) => [scope.current, ...animation])
				);
				args.onEnterAnimationComplete?.();
			};
			animateEntrance();
			args.onEnterTriggered?.();
		} else {
			const animateExit = async () => {
				await animate(scope.current, ...exitAnimation);
				args.onExitAnimationComplete?.();
				safeToRemove();
			};
			animateExit();
			args.onExitTriggered?.();
		}
	}, [
		isPresent,
		scope,
		animate,
		safeToRemove,
		enterAnimation,
		exitAnimation,
		args,
	]);
	return [scope];
}

export default useEnterExitAnimation;
